/**
 * <strong>Properties:</strong><br>
 * <br>
 * <li>{@link module:mw-form/mixins/property/DisabledMixin|disabled}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/EditableMixin|editable}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/FormatMixin|format}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/HorizontalAlignmentMixin|horizontalAlignment}</li>
 * <br>
 * <li>limits</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/PlaceholderMixin|placeholder}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/SizeMixin|width, height}</li>
 * <br>
 * <li><a href=#step>step</a></li>
 * <br>
 * <li><a href=#includeMaximum>includeMaximum</a></li>
 * <br>
 * <li><a href=#includeMinimum>includeMinimum</a></li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/TagMixin|tag}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/TextInputValueMixin|value}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/property/ValidationMixin|validationState, errorText, warningText}</li>
 * <br>
 * <br>
 * <strong>Events:</strong>
 * <br>
 * <br>
 * <li>{@link module:mw-form/mixins/event/ChangeEventMixin|change}</li>
 * <br>
 * <br>
 * <strong>Example:</strong>
 * <br>
 * <br>
 * For a working example, see the {@link http://inside-files/dev/eps/WIT/WebWidgets/Gallery/Gallery/|gallery}
 * <br>
 * <br>
 * @module mw-form/Spinner
 *
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */

// TODO: Remove _CSSStateMixin dependency, as seen in SplitButton and ToggleSplitButton

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-style",
    "dojo/dom-geometry",
    "dojo/dom-construct",
    "dojo/on",
    "dojo/keys",
    "dijit/_WidgetBase",
    "dijit/_Contained",
    "dijit/_CssStateMixin",
    "dijit/_FocusMixin",
    "dijit/_TemplatedMixin",
    "mw-form/TextField",
    "mw-form/mixins/property/TagMixin",
    "mw-form/mixins/property/DisabledMixin",
    "mw-form/mixins/property/EditableMixin",
    "mw-form/mixins/property/FormatMixin",
    "mw-form/mixins/property/SizeMixin",
    "mw-form/mixins/property/ValidationMixin",
    "mw-form/mixins/property/VisualFamilyMixin",
    "mw-form/mixins/property/HorizontalAlignmentMixin",
    "mw-form/mixins/event/ChangeEventMixin",
    "mw-form/mixins/event/TypingEventMixin",
    "mw-form/mixins/ResizeMixin",
    "mw-form/mixins/mixinDependencyValidator",
    "mw-utils/ValidationFunctions",

    "dojo/text!./templates/Spinner.html"

], function (declare, lang, domStyle, domGeom, domConstruct, on, keys, _WidgetBase, _Contained,
             _CssStateMixin, _FocusMixin, _TemplatedMixin, TextField, TagMixin, DisabledMixin,
             EditableMixin, FormatMixin, SizeMixin, ValidationMixin, VisualFamilyMixin,
             HorizontalAlignmentMixin, ChangeEventMixin, TypingEventMixin, ResizeMixin,
             mixinDependencyValidator, ValidationFunctions, template) {

    //Constants
    var BUTTON_HEIGHT = 6;
    var BUTTON_BORDER = 3;
    //g1951459 - Default width constants
    var DEFAULT_WIDTH = 150;
    var DEFAULT_BUTTON_WIDTH = 22;

    return declare(mixinDependencyValidator.validate([ _WidgetBase, _Contained, _CssStateMixin,
        _FocusMixin, _TemplatedMixin,
        TagMixin, DisabledMixin, EditableMixin, FormatMixin, SizeMixin, ValidationMixin,
        VisualFamilyMixin,
        HorizontalAlignmentMixin, ChangeEventMixin, TypingEventMixin, ResizeMixin]), {

        baseClass: "mwWidget mwSpinner",
        templateString: template,

        /* Overriding from  Size Mixin*/
        width: 150,

        height: 22,

        value: 100,

        /**
         * @property  {integer} step.
         * The value by which the spinner should increment or decrement
         * @instance
         * @default
         */

        step: 1,

        errorText: "",

        horizontalAlignment: "right",

        /**
         * @property  {boolean} roundValueToInt.
         * Determines if user entered fractional values will be rounded to integer
         * This property allows to restrict value to only integer
         * @instance
         * @default - false
         */
        roundValueToInt: false,

        /**
         * @property  {boolean} setDomValueOnButtonClick.
         * Determines if consumer of spinner wants to enable custom display and doesn't
         * want to set value in domNode while increment and decrement
         * When true, the value will be set in the widget's DOM after increment/decrement button click.
         * When false, the DOM value is not set. This would be desired behavior when the app author sets
         * a value in different format (using format api) in the increment/decrement and in this scenario
         * if this property will not be false then consumer will notice the flashy behavior because setting
         * dom value twice.
         * @instance
         * @default - false
         */
        setDomValueOnButtonClick: true,

        /**
         * @property  {int} valuePrecision.
         * Determines for fractional values what is the display value precision
         * @instance
         * @default - 4
         */
        valuePrecision: 4,

        /**
         * @property  {boolean} includeDefaultErrorText.
         * Determines if error messages will be pre-populated depending upon error type
         * @instance
         * @default - false
         */

        // Ideally this property value can be true by default but to lookout existing spinners with
        // custom messages keeping this property default value false
        includeDefaultErrorText: false,

        /**
         * @property  {boolean} isCustomRangeErrorMessages.
         * Determines if range error message will include the limits or show only generic range error
         * @instance
         * @default - true
         */
        isCustomRangeErrorMessages: true,

        /**
         * @property  {String} invalidMessage.
         * Determine error message to display if value is invalid.
         * @instance
         * @default - Value must be numeric
         */
        invalidMessage: "Value must be numeric",

        /**
         * @property  {String} rangeMessage.
         * Determine default error message to display if value is out of range.
         * @instance
         * @default - Value entered is out of range
         */
        rangeMessage: "Value entered is out of range",

        /**
         * @property  {String} rangeMessageBetween.
         * Determine range error message if isCustomRangeErrorMessages is true.
         * @instance
         * @default - Value must be between {lowerLimit} and {upperLimit}
         */
        rangeMessageBetween: "Value must be between %1 and %2",

        /**
         * @property  {String} rangeMessageLessThan.
         * Determine range error message if value is less than lower limit and
         * isCustomRangeErrorMessages is true and includeMinimum is false
         * @instance
         * @default - Value must be less than {upperLimit}
         */
        rangeMessageLessThan: "Value must be less than %1",

        /**
         * @property  {String} rangeMessageLessThan.
         * Determine range error message if value is more than upper limit and
         * isCustomRangeErrorMessages is true and includeMaximum is false
         * @instance
         * @default - Value must be greater than {lowerLimit}
         */
        rangeMessageGreaterThan: "Value must be greater than %1",

        /**
         * @property  {boolean} includeMinimum.
         * Determines if the value reaches the minimum value or not
         * @instance
         * @default - true
         */
        includeMinimum: true,

        /**
         * @property  {boolean} includeMaximum.
         * Determines if the value reaches the maximum value or not
         * @instance
         * @default - true
         */
        includeMaximum: true,

        /**
         * @property  {boolean} allowInfiniteValue.
         * Determines if the value entered in spinner can be Infinite (-Inf or Inf)
         * @instance
         * @default - false
         */
        allowInfiniteValue: false,

        //TO prevent normal click in the case of long click
        _preventClick: false,

        // To track if mouse is pressed
        _isMouseDown: false,

        // To track if mouse is pressed
        _isKeyDown: false,

        //private properties to calculate increment and decrement
        _startValue: 100,
        _clickCount: 0,
        _previousValue: 100,

        constructor: function () {
            this._textField = new TextField({horizontalAlignment: "right"});
            this.focusNode = this._textField.focusNode;
            this.inputNode = this._textField.inputNode;
            this.own(this._textField);
            this.limits = {minimum: 0, maximum: Number.POSITIVE_INFINITY};
            //Adding a watch to trigger set on spinner widget
            //Otherwise watch on spinner does not work due to composition
            this.own(this._textField.watch("value",
                lang.hitch(this, function (value, oldValue, newValue) {
                    if (!isNaN(parseFloat(newValue))) {
                        this._set("value", parseFloat(newValue));
                    }
                })));
        },

        buildRendering: function () {
            this.inherited(arguments);
            this._textField.placeAt(this.containerNode, "first");
        },

        /* Override from Editable Mixin */
        /* Forward to textField*/
        _setEditableAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Editable property expects a boolean");
            }
            this._textField.set("editable", value);

            if (!value) {
                this.domNode.classList.add("mwNonEditable");
            } else {
                this.domNode.classList.remove("mwNonEditable");
            }

            this._set("editable", value);
        },

        _setDisabledAttr: function (value) {
            this.inherited(arguments);
            // g2158341 - Spinner disable in error state set the value as error value
            if(value && this.get("validationState") !== "normal") {
                this._textField.set("value", this._previousValue + "");
                this.set("validationState", "normal");
            }
            this._textField.set("disabled", value);
        },

        //Set horizontal alignment
        _setHorizontalAlignmentAttr: function (value) {
            this.inherited(arguments);
            this._textField.set("horizontalAlignment", value);
        },

        _setStepAttr: function (step) {
            if (typeof step !== "number") {
                throw new Error("'step' property expects a Number!");
            }
            this._set("step", step);
            this._setButtonState();
            //g2007457 - _startValue and _clickCount should be reset when setting step size
            this._startValue = this.get("value");
            this._clickCount = 0;
        },

        _setValueAttr: function (value) {
            if (!ValidationFunctions.isEmpty(value) && ((value < this.get("limits").minimum) ||
                    (value > this.get("limits").maximum))) {
                throw new Error("'Value' must be numeric and fall within the range of 'Limits'.");
            }
            this._startValue = parseFloat(value);
            this._previousValue = parseFloat(value);
            this._clickCount = 0;

            this._textField.set("value", value + "");
            this._set("value", value);
            this._setButtonState();
        },

        _getValueAttr: function() {
            return this.value;
        },

        /* Extension of size Mixin */

        _setWidthAttr: function (width) {
            this.inherited(arguments);
            this.resize();
        },

        _setHeightAttr: function (value) {
            this.inherited(arguments);
            this._textField.set("height", value);
            this._resizeHeight(value);
        },

        _setLimitsAttr: function (limits) {
            if (typeof limits !== "object") {
                throw new Error("'IncludeMinimum' property expects an 'Object'!");
            }
            this._validateLimits(limits);
            this._set("limits", limits);
            this._applyConstraintsToValue();
        },

        _validateLimits: function (limits) {
            if (typeof limits.maximum !== "number" || typeof limits.minimum !== "number") {
                throw new Error("'Limits' expects an Object of numbers!");
            }
            if (limits.minimum > limits.maximum) {
                throw new Error("'Minimum' should be less than maximum");
            }
        },

        _setIncludeMinimumAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("'IncludeMinimum' property expects a 'Boolean'!");
            }
            this._set("includeMinimum", value);
            this._applyConstraintsToValue();
        },

        _setIncludeMaximumAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("'IncludeMaximum' property expects a 'Boolean'!");
            }
            this._set("includeMaximum", value);
            this._applyConstraintsToValue();
        },

        _applyConstraintsToValue: function () {
            var constraintMaxValue = this.get("includeMaximum") ? this.get("limits").maximum :
                this.get("limits").maximum - this.get("step");
            constraintMaxValue = this._convertInfiniteValueToString(constraintMaxValue);
            var constraintMinValue = this.get("includeMinimum") ? this.get("limits").minimum :
                this.get("limits").minimum + this.get("step");
            constraintMinValue = this._convertInfiniteValueToString(constraintMinValue);
            var currentVal = this.get("value");
            currentVal = ValidationFunctions.convertValueToNumber(currentVal)
            if (this.get("limits").maximum <= currentVal && this._rangeCheck(constraintMaxValue)) {
                this.set("value", constraintMaxValue);
            }
            if (this.get("limits").minimum >= this.get("value") && this._rangeCheck(constraintMinValue)) {
                this.set("value", constraintMinValue);
            }
            this._setButtonState();
        },

        // Disables/Enables the Up/Down Buttons because the potential next value based on the current value and step
        // could be outside the allowed range
        // It accounts for inclusivity rules of the Min/Max and also Rounding
        //
        // Called after:
        // 1. Configuration change - constraints(min, max, includeMin, includeMax) /value/step
        // 2. User interaction with the Up/Down buttons
        _setButtonState: function () {
            var currentVal = this.get("value");
            var isUpButtonEnabled;
            var isDownButtonEnabled;

            if(!ValidationFunctions.isEmpty(currentVal)) {

                currentVal = ValidationFunctions.convertValueToNumber(currentVal);

                // The below disables the buttons based on the logic of if the result of adding/subtracting
                // the step with current value
                var isNextUpperValLessThanMax = this._rangeCheck(currentVal + this.step) && (currentVal + this.step) < this.get("limits").maximum,
                    isCurrentValueWithinMinMax = this._rangeCheck(currentVal),
                    isNextLowerValGreaterThanMin = this._rangeCheck(currentVal - this.step) && (currentVal - this.step) > this.get("limits").minimum;


                // Account for inclusivity rules and Rounding
                if (this.get("includeMaximum")) {
                    // is current value same as max
                    var isCurrentValueEqualToMax = currentVal === this.get("limits").maximum;
                    var canSnapToMax = isCurrentValueWithinMinMax && !isCurrentValueEqualToMax;
                } else {
                    var increment = this._calculateIncrement(this.get("limits").maximum, -1 / 10);
                    // is current value close to max
                    var isCurrentValueCloseToMax = (currentVal >= this.get("limits").maximum + increment);
                    canSnapToMax = isCurrentValueWithinMinMax && !isCurrentValueCloseToMax && !this.get("roundValueToInt");
                }

                if (this.get("includeMinimum")) {
                    // is current value same as min
                    var isCurrentValueEqualToMin = currentVal === this.get("limits").minimum;
                    var canSnapToMin = isCurrentValueWithinMinMax && !isCurrentValueEqualToMin;
                } else {
                    increment = this._calculateIncrement(this.get("limits").minimum, 1 / 10);
                    // is current value close to min
                    var isCurrentValueCloseToMin = (currentVal <= this.get("limits").minimum + increment);
                    canSnapToMin = isCurrentValueWithinMinMax && !isCurrentValueCloseToMin && !this.get("roundValueToInt");
                }

                isUpButtonEnabled = (isNextUpperValLessThanMax || canSnapToMax);
                isDownButtonEnabled = (isNextLowerValGreaterThanMin || canSnapToMin);
            } else {
                isUpButtonEnabled = true;
                isDownButtonEnabled = true;
            }

            isUpButtonEnabled ? this._enableIncrementButton() : this._disableIncrementButton();
            isDownButtonEnabled ? this._enableDecrementButton() : this._disableDecrementButton();
        },

        _getDomNodeForValidationClasses: function () {
            return this.domNode;
        },

        _getParentPopup: function (node) {
            while (node) {
                if (node.classList && node.classList.contains("dijitPopup")) {
                    return node;
                }
                node = node.parentNode;
            }
            return null;
        },

        postCreate: function () {
            this.inherited(arguments);
            //When the default value is falsy, the setter will not be triggered during the widget's construction phase
            // We need to call the setter explicitly
            this.set("value", this.value);
            this.own(
                //Use keydown to stop Pageup from moving entire page up
                //Can hold down keys to change spinner values
                on(this.domNode, "keydown", lang.hitch(this, "_onKeyDown")),
                on(this.domNode, "keyup", lang.hitch(this, "_onKeyUp")),
                on(this.incrementButtonNodeWrapper, "click", lang.hitch(this, function () {
                    if (!this._preventClick && !this._isIncrementButtonDisabled()) {
                        var oldValue = parseFloat(this._textField.get("value"));
                        this._handleIncrement();
                        var newValue = parseFloat(this._textField.get("value"));
                        this.changingActionPerformed(newValue);
                        this._triggerChangeEvent("value", oldValue, newValue);
                    }
                    this._preventClick = false;
                })),
                on(this.decrementButtonNodeWrapper, "click", lang.hitch(this, function () {
                    if (!this._preventClick && !this._isDecrementButtonDisabled()) {
                        var oldValue = parseFloat(this._textField.get("value"));
                        this._handleDecrement();
                        var newValue = parseFloat(this._textField.get("value"));
                        this.changingActionPerformed(newValue);
                        this._triggerChangeEvent("value", oldValue, newValue);
                    }
                    this._preventClick = false;
                })),

                /******* g1942843 - Start Changes **********/
                on(this.incrementButtonNodeWrapper, "contextmenu",
                    function(e){
                        e.preventDefault();
                        return false;
                    }.bind(this)),

                on(this.decrementButtonNodeWrapper, "contextmenu",
                    function(e){
                        e.preventDefault();
                        return false;
                    }.bind(this)),

                on(this.incrementButtonNodeWrapper, "mousedown",
                    function(e){
                        if(e.button === 0) {
                            this._onIncrementButtonMouseDown();
                        }
                    }.bind(this)),

                on(this.decrementButtonNodeWrapper, "mousedown",
                    function(e){
                        if(e.button === 0) {
                            this._onDecrementButtonMouseDown();
                        }
                    }.bind(this)),

                on(this.incrementButtonNodeWrapper, "mouseup, mouseout",
                    function(e){
                        if(e.button === 0) {
                            this._onButtonMouseUp();
                        }
                    }.bind(this)),

                on(this.decrementButtonNodeWrapper, "mouseup, mouseout",
                    function(e){
                        if(e.button === 0) {
                            this._onButtonMouseUp();
                        }
                    }.bind(this)),

                /******* g1942843 - End Changes **********/

                on(this._textField, "focusin", lang.hitch(this._textField, function () {
                    var parent = this.getParent();
                    if (!parent.get("disabled") && parent.get("editable")) {
                        if (parent.get("validationState") === "normal") {
                            if (!parent._isInt(this.get("value")) || !parent._isInt(parent.get("step"))) {
                                this.setValueInDom(parent._formatShort(parent.get("value")));
                            } else {
                                this.setValueInDom(parent.get("value"));
                            }
                        }
                        this.inputNode.select();
                    }
                }))
            );

            //When you type and press enter
            this._textField.on("change", lang.hitch(this, function (event) {
                this._onChangeHandler(event);
            }));

            this._textField.on("typing", lang.hitch(this, function (event) {
                this._handleValidation(event.mwEventData.currentContent);
            }));

            //When you drag
            this._textField.inputNode.addEventListener("dragstart", this._disableDragDrop);

            //When you drop
            this._textField.inputNode.addEventListener("drop", this._disableDragDrop);
        },

        startup: function () {
            // this._textField.startup() must be called before this.resize(), and since the
            // this.inherited(arguments) may call this.resize() in ResizeMixin, the order matters.
            this._textField.startup();
            this.inherited(arguments);
        },

        resize: function () {
            // this.inherited(arguments) may call ResizeMixin's resize() method which calls resize()
            // on the child widgets (TextField).  Since the resize() methods of the children could
            // depend on the sizing of the parent, then the parent must be resized first.
            var currentWidth = domGeom.position(this.domNode).w;
            var buttonsWidth = domGeom.position(this.buttonsContainerNode).w;

            //g1951459 - Assigning default width if dom is not ready
            currentWidth = currentWidth === 0 ? DEFAULT_WIDTH : currentWidth;
            buttonsWidth = buttonsWidth === 0 ? DEFAULT_BUTTON_WIDTH : buttonsWidth;

            if (buttonsWidth === 0) {
                //find parent popup
                var popup = this._getParentPopup(this.buttonsContainerNode);
                if (popup !== null) {
                    //add offscreen
                    popup.classList.add("dijitOffScreen");
                    //store display value
                    var currentDisplay = domStyle.get(popup, "display");
                    //remove display none
                    popup.style.display = "block";
                    //compute width
                    buttonsWidth = domGeom.position(this.buttonsContainerNode).w;
                    //restore display value
                    popup.style.display =  currentDisplay;
                }
            }

            var textFieldBorderContents = domGeom.getPadBorderExtents(this._textField.domNode);
            var buttonsBorderContents = domGeom.getPadBorderExtents(this.buttonsContainerNode);
            var buttonsPaddingBox = buttonsBorderContents.l +
                buttonsBorderContents.r;

            this.containerNode.style.width = currentWidth - buttonsWidth + "px";
            this._textField.domNode.style.width =
                currentWidth - buttonsWidth  - buttonsPaddingBox + "px";

            this.inherited(arguments);
        },

        _resizeHeight: function(value) {
            // g1924307 - In Logic Analzer and Icon Editor Spinner Geometry is not present
            // When height gets calculated so constant values (BUTTON_HEIGHT and BUTTON_BORDER)
            // is used to cover those scenarios

            var currentHeight = domGeom.position(this.domNode).h;
            var upButtonHeight = domGeom.position(this.incrementButtonNode).h;
            var downButtonHeight = domGeom.position(this.decrementButtonNode).h;

            currentHeight = currentHeight === 0 ? value : currentHeight;
            upButtonHeight = upButtonHeight === 0 ? BUTTON_HEIGHT : upButtonHeight;
            downButtonHeight = downButtonHeight === 0 ? BUTTON_HEIGHT : downButtonHeight;

            var reqButtonWrapperHeight = (currentHeight - BUTTON_BORDER) / 2;
            var reqUpButtonTopMargin = (reqButtonWrapperHeight - upButtonHeight)/2 - 1;
            var reqDownButtonTopMargin = (reqButtonWrapperHeight - downButtonHeight)/2 + 1;

            this.containerNode.style.height = currentHeight + "px";
            this._textField.domNode.style.height = currentHeight + "px";

            this.buttonsContainerNode.style.height = currentHeight + "px";
            this.incrementButtonNodeWrapper.style.height = reqButtonWrapperHeight + "px";
            this.decrementButtonNodeWrapper.style.height = reqButtonWrapperHeight + "px";
            this.incrementButtonNode.style.marginTop = reqUpButtonTopMargin + "px";
            this.decrementButtonNode.style.marginTop = reqDownButtonTopMargin + "px";
        },

        _onChangeHandler: function (event) {
            var oldValue, newValue;
            //Dont let textField change event bubble up
            event.preventDefault();
            event.stopPropagation();
            if (this.get("validationState") !== "normal") {
                this._revertToOldValue(event.mwEventData.oldValue);
            } else {
                //Emit from widget with Numeric value
                oldValue = event.mwEventData.oldValue;
                newValue = event.mwEventData.newValue;
                if (this.roundValueToInt) {
                    // Number entered by user
                    var rawValue = this._getTextFieldContentsAsFiniteNumber();
                    newValue = this._validateAndCalibrateRoundedValue(rawValue);
                    this._startValue = parseFloat(newValue);
                    this._clickCount = 0;
                }
                newValue = this._isNumeric(newValue) ? parseFloat(newValue) : newValue;
                oldValue = this._isNumeric(oldValue) ? parseFloat(oldValue) : oldValue;
                this.changingActionPerformed(newValue);
                this.set("value", newValue);
                this._triggerChangeEvent("value", oldValue, newValue);
            }

        },

        _disableDragDrop: function (event) {
            event.preventDefault();
            return false;
        },

        _onIncrementButtonMouseDown: function () {
            this._delayStart = setTimeout(lang.hitch(this, function () {
                this._valueChangeTimer = setInterval(lang.hitch(this, function () {
                    if (!this._isIncrementButtonDisabled()) {
                        var oldValue = parseFloat(this._textField.get("value"));
                        this._handleIncrement();
                        var newValue = parseFloat(this._textField.get("value"));
                        this._triggerChangingEvent("value", oldValue, newValue);
                    }
                }), 100);
                this._preventClick = true;
                this._isMouseDown = true;
            }), 300);
        },


        _onDecrementButtonMouseDown: function () {
            this._delayStart = setTimeout(lang.hitch(this, function () {
                this._valueChangeTimer = setInterval(lang.hitch(this, function () {
                    if (!this._isDecrementButtonDisabled()) {
                        var oldValue = parseFloat(this._textField.get("value"));
                        this._handleDecrement();
                        var newValue = parseFloat(this._textField.get("value"));
                        this._triggerChangingEvent("value", oldValue, newValue);
                    }
                }), 100);
                this._preventClick = true;
                this._isMouseDown = true;
            }), 300);
        },

        _onButtonMouseUp: function () {
            clearInterval(this._valueChangeTimer);
            clearTimeout(this._delayStart);
            if(this._isMouseDown) {
                //Emit from widget with new value
                this._triggerChangeEvent("value",'',this.get("value"));
                this._isMouseDown = false;
            }
        },

        _onKeyDown: function (event) {
            if (event.keyCode === keys.UP_ARROW) {
                //Stop entire page from moving
                event.preventDefault();
                if (!this._isIncrementButtonDisabled()) {
                    var oldValue = parseFloat(this._textField.get("value"));
                    this._handleIncrement();
                    var newValue = parseFloat(this._textField.get("value"));
                    this._triggerChangingEvent("value", oldValue, newValue);
                    this._isKeyDown = true;
                }
            } else if (event.keyCode === keys.DOWN_ARROW) {
                //Need preventDefault to stop cursor moving to beginning of text
                event.preventDefault();
                if (!this._isDecrementButtonDisabled()) {
                    var oldValue = parseFloat(this._textField.get("value"));
                    this._handleDecrement();
                    var newValue = parseFloat(this._textField.get("value"));
                    this._triggerChangingEvent("value", oldValue, newValue);
                    this._isKeyDown = true;
                }
            } else if (event.keyCode === keys.PAGE_UP) {
                //Need preventDefault to stop page up of the page
                event.preventDefault();
            } else if (event.keyCode === keys.PAGE_DOWN) {
                //Need preventDefault to stop page down of the page
                event.preventDefault();
            }
        },

        // overriding the _onKeyUp event listener from TextField
        _onKeyUp: function (event) {
            this.inherited(arguments);
            if (event.keyCode === keys.UP_ARROW) {
                if(this._isKeyDown) {
                    //Emit from widget with new value
                    this._triggerChangeEvent("value",'',this.get("value"));
                    this._isKeyDown = false;
                }
            } else if (event.keyCode === keys.DOWN_ARROW) {
                if(this._isKeyDown) {
                    //Emit from widget with new value
                    this._triggerChangeEvent("value",'',this.get("value"));
                    this._isKeyDown = false;
                }
            }

        },

        _triggerChangingEvent: function (property, oldValue, newValue) {
            this.emit("changing", {
                mwEventData: {
                    propertyName: property,
                    oldValue: oldValue,
                    newValue: newValue
                }
            });
        },

        _handleValidation: function (value) {
            //Check in range and check if string has only numbers
            //NAN if empty string
            var isInRange = this._rangeCheck(ValidationFunctions.convertValueToNumber(value));
            var isNumeric = this.allowInfiniteValue ? ValidationFunctions.isFiniteValue(value) || ValidationFunctions.isInfinteValue(value) : ValidationFunctions.isFiniteValue(value) && !isNaN(parseFloat(value));
            var isValid = this.disabled || (isNumeric && isInRange);

            if(this.includeDefaultErrorText) {
                if (isNumeric) {
                    //Out of range messages
                    if (!isInRange && this.isCustomRangeErrorMessages) {  //if the error messages for range are user-specified
                        var minForToolTip = (this.get("limits").minimum === ValidationFunctions.NegativeInfinity) ? ValidationFunctions.NegativeInfinityString : this.get("limits").minimum,
                            maxForTooltip = (this.get("limits").maximum === ValidationFunctions.PositiveInfinity) ? ValidationFunctions.PositiveInfinityString : this.get("limits").maximum;

                        if (value === this.get("limits").minimum && !this.get("includeMinimum")) { //if the user entered value is the specified min but the lower limit is not inclusive
                            this.set("errorText", this.rangeMessageGreaterThan.replace("%1", minForToolTip));
                        } else if (value === this.get("limits").maximum && !this.get("includeMaximum")) {  //if the user entered value is the specified max but the upper limit is not inclusive
                            this.set("errorText", this.rangeMessageLessThan.replace("%1", maxForTooltip));
                        } else {  //basic message
                            this.set("errorText", this.rangeMessageBetween.replace("%1", minForToolTip).replace("%2", maxForTooltip));
                        }
                    } else { // use the widget default range message
                        this.set("errorText", this.rangeMessage);
                    }
                } else {  //when user enters non-numeric characters
                    this.set("errorText", this.invalidMessage);
                }
            }

            if(!isValid) {
                this.set("validationState", "error");
            } else {
                this.set("validationState", "normal");
                if (this.validationTooltip) {
                    this.validationTooltip.domNode.classList.remove("mwValidationFocused");
                }
            }
            return isValid;
        },

        /**
         * Handle Increment when up button is clicked or up key is pressed
         *
         * This function handle 3 use cases
         * 1) When the current spinner value is finite
         * 2) When the current spinner value is Infinite (Inf or -Inf)
         *    a) When Limits are [-inf, inf] and current value is -inf then
         *       Decrement button will be disabled and by pressing up button
         *       value will change to 0 and both button will be enabled
         *    b) When Limits are [-inf, inf] and current value is inf then
         *       Increment button will be disabled and by pressing up button
         *       value will not change
         *    c) When Limits are [-inf, n] and current value is -inf then
         *       decrement button will be disabled and by pressing up button
         *       value will change to n (where n is finite upper limit)
         *    d) When Limits are [n, inf] and current value is inf then
         *       Increment button will be disabled and by pressing up button
         *       value will not change
         * 3) When the current spinner value is blank (empty)
         *    a) if lower limit is finite then by pressing up button value will
         *       set to lower limit + step
         *    b) If lower limit is not finite (-Inf) but upper limit is finite
         *       then up button value will set to upper limit
         *    c) If both limits are not finite (-inf, inf) then by pressing
         *       up button value will set to upper 0 + step
         */
        _handleIncrement: function () {
            var newValue;
            //Handling increment with currentValue as -Inf
            if(ValidationFunctions.isInfinteValue(this._convertInfiniteValueToString(this.get("value")))) {
                newValue = (this.get("limits").maximum === Number.POSITIVE_INFINITY) ? 0 : this.get("limits").maximum;
                this._startValue = newValue;
                this._clickCount = 0;
            } else if (ValidationFunctions.isEmpty(this.get("value"))) { // Handling increment with empty current value
                if (ValidationFunctions.isFiniteValue(this.get("limits").minimum)) {
                    newValue = this.get("limits").minimum + this.get("step");
                } else if (ValidationFunctions.isFiniteValue(this.get("limits").maximum)) {
                    newValue = this.get("limits").maximum;
                } else {
                    newValue = 0 + this.get("step");
                }
                this._startValue = newValue;
                this._clickCount = 0;
            } else { // handling increment with finite current value
                this._clickCount = this._clickCount + 1;
                newValue = this._startValue + this.get("step") * this._clickCount;
            }

            if (!this._rangeCheck(newValue)) {
                if (this.get("includeMaximum")) {
                    newValue = this.get("limits").maximum;
                    newValue = this._convertInfiniteValueToString(newValue);
                } else {
                    //Make the value as close as possible to the maximum
                    var epsilon = this._calculateIncrement(this.get("limits").maximum, -1 / 10);
                    newValue = this.get("limits").maximum + epsilon;
                    newValue = this._convertInfiniteValueToString(newValue);
                    if (this.roundValueToInt) {
                        newValue = this._validateAndCalibrateRoundedValue(newValue);
                    }
                }
                this._startValue = newValue;
                this._clickCount = 0;
            }
            this._textField._set("value", newValue + "");
            this._set("value", newValue);
            this._previousValue = newValue;
            // g1881724 - for more information related to setDomValueOnButtonClick
            if(this.setDomValueOnButtonClick) {
                //Format short display
                if (!ValidationFunctions.isInfinteValue(newValue) && !this._isInt(newValue)) {
                    this._textField.setValueInDom(this._formatShort(newValue) + "");
                } else {
                    this._textField.setValueInDom(newValue + "");
                }
            }
            this._setButtonState();
        },


        /**
         * Handle Decrement when down button is clicked or down key is pressed
         *
         * This function handle 3 use cases
         * 1) When the current spinner value is finite
         * 2) When the current spinner value is Infinite (Inf or -Inf)
         *    a) When Limits are [-inf, inf] and current value is inf then
         *       increment button will be disabled and by pressing up down
         *       value will change to 0 and both button will be enabled
         *    b) When Limits are [-inf, inf] and current value is -inf then
         *       decrement button will be disabled and by pressing down button
         *       value will not change
         *    c) When Limits are [n, Inf] and current value is inf then
         *       increment button will be disabled and by pressing down button
         *       value will change to n (where n is finite lower limit)
         *    d) When Limits are [-inf, n] and current value is -inf then
         *       decrement button will be disabled and by pressing down button
         *       value will not change
         * 3) When the current spinner value is blank (empty)
         *    a) if lower limit is finite then by pressing down button value will set to lower limit
         *    b) If lower limit is not finite (-Inf) but upper limit is finite then down button
         *        value will set to upper limit - step
         *    c) If both limits are not finite (-inf, inf) then by pressing down button
         *         value will set to upper 0 - step
         */
        _handleDecrement: function () {
            var newValue;
            //Handling decrement with currentValue as Inf
            if(ValidationFunctions.isInfinteValue(this._convertInfiniteValueToString(this.get("value")))) {
                newValue = (this.get("limits").minimum === Number.NEGATIVE_INFINITY) ? 0 : this.get("limits").minimum;
                this._startValue = newValue;
                this._clickCount = 0;
            } else if (ValidationFunctions.isEmpty(this.get("value"))) { // Handling decrement with empty current value
                if (ValidationFunctions.isFiniteValue(this.get("limits").minimum)) {
                    newValue = this.get("limits").minimum;
                } else if (ValidationFunctions.isFiniteValue(this.get("limits").maximum)) {
                    newValue = this.get("limits").maximum - this.get("step");
                } else {
                    newValue = 0 - this.get("step");
                }
                this._startValue = newValue;
                this._clickCount = 0;
            } else {  // handling decrement with finite current value
                this._clickCount = this._clickCount - 1;
                newValue = this._startValue + this.get("step") * this._clickCount;
            }

            if (!this._rangeCheck(newValue)) {
                if (this.get("includeMinimum")) {
                    newValue = this.get("limits").minimum;
                    newValue = this._convertInfiniteValueToString(newValue);
                } else {
                    //Make the value as close as possible to the maximum
                    var epsilon = this._calculateIncrement(this.get("limits").minimum, 1 / 10);
                    newValue = this.get("limits").minimum + epsilon;
                    newValue = this._convertInfiniteValueToString(newValue);
                    if (this.roundValueToInt) {
                        newValue = this._validateAndCalibrateRoundedValue(newValue);
                    }
                }
                this._startValue = newValue;
                this._clickCount = 0;
            }
            this._textField._set("value", newValue + "");
            this._set("value", newValue);
            this._previousValue = newValue;
            // g1881724 - for more information related to setDomValueOnButtonClick
            if(this.setDomValueOnButtonClick) {
                //Format short display
                if (!ValidationFunctions.isInfinteValue(newValue) && !this._isInt(newValue)) {
                    this._textField.setValueInDom(this._formatShort(newValue) + "");
                } else {
                    this._textField.setValueInDom(newValue + "");
                }
            }
            this._setButtonState();
        },

        // Find an increment that we can add/subtract with the non-included limit such that
        // the value is within the limits.
        // Start the increment at .1, then go to .01, etc...
        // until the value is within the limits
        _calculateIncrement: function (limit, startIncr) {
            var increment = startIncr;
            if (!ValidationFunctions.isFiniteValue(limit)) {
                return 0;
            }

            while (this._rangeCheck(limit + increment) === false) {
                increment = increment / 10;
            }
            return increment;
        },

        /**
         * Validates user entry and enables error affordance
         */
        _validateAndCalibrateRoundedValue: function (rawValue) {

            // Validated rounded value can be one of three choices
            var rounded = Math.round(rawValue);
            var bottom = Math.floor(rawValue);
            var top = Math.ceil(rawValue);

            var calibratedValue = null;

            if (this._rangeCheck(rounded)) {
                calibratedValue = rounded;
            } else if (this._rangeCheck(bottom)) {
                calibratedValue = bottom;
            } else if (this._rangeCheck(top)) {
                calibratedValue = top;
            }
            return this._convertInfiniteValueToString(calibratedValue);
        },

        /**
         * Check if the value is within the specified bounds of min, max alongwith includeMinimum and includeMaximum flags
         * @param value to check
         * @return boolean
         */
        _rangeCheck: function (value) {
            var minCompare = (this.get("includeMinimum") === false) ? value > this.get("limits").minimum : value >= this.get("limits").minimum,
                maxCompare = (this.get("includeMaximum") === false) ? value < this.get("limits").maximum : value <= this.get("limits").maximum,
                // Very large numbers (positive and negative) are special cased because comparisons like <> are not valid
                isMaxValue = ((this.get("limits").maximum - value) === 0) && ((Math.abs(this.get("limits").maximum) - Number.MAX_VALUE) === 0),
                isMinValue = ((this.get("limits").minimum - value) === 0) && ((Math.abs(this.get("limits").minimum) - Number.MAX_VALUE) === 0);
            return minCompare && maxCompare || isMaxValue || isMinValue;
        },

        _setRevertTimeout: function(value) {
            return value || 750; // g2052711 based on UX input, set default time to revert bad text input to 750ms
        },

        _revertToOldValue: function (oldValue) {
            //Revert to old value if widget is in error state
            // Typing event already occured, so widget should be in either error or normal state
            this._hiddenDiv = domConstruct.create("div", {
            }, this.domNode);
            var borderExtents = domGeom.getBorderExtents(this.domNode);
            //Assuming the parent has position relative as DisabledMixin adds it
            this._hiddenDiv.style.top = -borderExtents.t + "px";
            this._hiddenDiv.style.right = -borderExtents.r + "px";
            this._hiddenDiv.style.bottom = -borderExtents.b + "px";
            this._hiddenDiv.style.left = -borderExtents.l + "px";
            this._hiddenDiv.style.position = "absolute";

            this._hiddenDiv.addEventListener("mousedown", function(e) {
                e.preventDefault();
                e.stopPropagation();
            });

            setTimeout(lang.hitch(this, function () {
                //Widget can be destroyed before this code can be hit because of setTimeout
                //Also value can be changed because of valid value change before
                //this code can be hit because of setTimeout

                //using text field value for validation instead of spinner value to do the validation
                //on value before applying parseFloat function. see geck - g1813647
                var currentValue = this._textField.get("value");
                if (this._destroyed !== true) {
                    if(!this._handleValidation(currentValue)) {
                        this._textField.set("value", oldValue + "");
                        if(!ValidationFunctions.isFiniteValue(oldValue)) {
                            this._set("value", oldValue);
                        }
                        this._handleDisplayFormat();
                    }
                    this.set("validationState", "normal");
                    domConstruct.destroy(this._hiddenDiv);
                }
            }), this._setRevertTimeout());

        },

        /**
         * @function changingActionPerformed
         * @summary this method should be overridden to handle any additional actions required along with change event
         * @desc Use case - uispinner needs to emit changing event along with change event on value change
         */
        changingActionPerformed: function (newValue) {
        },

        /**
         * Returns the contents in the textField as a JavaScript Number. Inf is translated to Number.INFINITY
         * @return {Number}
         * @private
         */
        _getTextFieldContentsAsFiniteNumber: function () {

            var textFieldStringValue = this._getTextFieldContentsAsString();
            return ValidationFunctions.convertValueToNumber(textFieldStringValue);
        },
        /**
         * Returns the contents of the textField as a JavaScript String after trimming whitespaces
         * @return String
         * @private
         */
        _getTextFieldContentsAsString: function () {
            return lang.trim(this._textField.value);
        },

        _isDecrementButtonDisabled: function() {
            return this.decrementButtonNode.classList.contains("mwDecrementButtonDisabled");
        },

        _isIncrementButtonDisabled: function() {
            return this.incrementButtonNode.classList.contains("mwIncrementButtonDisabled");
        },

        _disableIncrementButton: function() {
            this.incrementButtonNode.classList.add("mwIncrementButtonDisabled");
            this.incrementButtonNodeWrapper.classList.add("mwButtonWrapperDisabled");
        },

        _disableDecrementButton: function() {
            this.decrementButtonNode.classList.add("mwDecrementButtonDisabled");
            this.decrementButtonNodeWrapper.classList.add("mwButtonWrapperDisabled");
        },

        _enableIncrementButton: function() {
            this.incrementButtonNode.classList.remove("mwIncrementButtonDisabled");
            this.incrementButtonNodeWrapper.classList.remove("mwButtonWrapperDisabled");
        },

        _enableDecrementButton: function() {
            this.decrementButtonNode.classList.remove("mwDecrementButtonDisabled");
            this.decrementButtonNodeWrapper.classList.remove("mwButtonWrapperDisabled");
        },

        _containsNonNumericCharacter: function (value) {

            return value.split("").some(function (character) {
                //+ and - are not valid as DOJO spinner also doesnt allow + or - => how about 5+5?

                return "0123456789.".indexOf(character) === -1;
            });

        },

        _isNumeric: function (num) {
            return !isNaN(parseFloat(num)) && isFinite(num);
        },

        _isInt: function (n) {
            return Number(n) === n && n % 1 === 0;
        },

        _formatShort: function (value) {
            if(ValidationFunctions.isFiniteValue(value)) {
                value = parseFloat(value);
                return ValidationFunctions.getPrecision(value) > this.valuePrecision ? parseFloat(value.toFixed(this.valuePrecision)) : value;
            } else {
                return value;
            }
        },

        _convertInfiniteValueToString: function (value) {
            var val = ValidationFunctions.convertValueToNumber(value);
            if(val === Number.POSITIVE_INFINITY) {
                return "Inf";
            } else if(val === Number.NEGATIVE_INFINITY) {
                return "-Inf";
            }
            return val;
        },

        _qeTypeOverWithEnter: function (value) {
            // _WidgetBase's emit method
            var qeOldValue = this.get("value");
            this.set("value", value);
            this._triggerChangeEvent("value", qeOldValue, value);
        }
    });
});